"use client";

import { useRef } from "react";
import Image from "next/image";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shadcn/ui/accordion";
import BlackPill from "@/components/getting-started/BlackPill";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";

import { PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";

import gridGBimg from "@/public/lp2/faq/grid-bg.svg";
import blurBGimg from "@/public/lp2/faq/blur-bg.svg";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger, useGSAP);
}

export default function FAQSection({ data }: { data: PageHomeSchema }) {
  const containerRef = useRef<HTMLDivElement | any>(undefined);
  const content1Ref = useRef<HTMLDivElement | any>(undefined);
  const content2Ref = useRef<HTMLDivElement | any>(undefined);

  useGSAP(
    () => {
      const faqTL = gsap.timeline({
        defaults: { opacity: 0, ease: "power2.inOut", duration: 0.25 },
      });

      faqTL
        .from(containerRef.current, {
          y: "2rem",
          delay: 0.5,
          scrollTrigger: {
            trigger: containerRef.current,
            end: "center center",
            scrub: 1,
          },
        })
        .from(content1Ref.current.children, {
          y: "2rem",
          delay: 0.5,
          stagger: 0.1,
          scrollTrigger: {
            trigger: content1Ref.current,
            end: "center center",
            scrub: 1,
          },
        })
        .from(content2Ref.current.children, {
          y: "2rem",
          delay: 0.5,
          stagger: 0.1,
          scrollTrigger: {
            trigger: content2Ref.current,
            end: "center center",
            scrub: 1,
          },
        });
    },
    { scope: containerRef }
  );

  return (
    <section
      ref={containerRef}
      className="w-full z-[1] relative mix-blend-lighten"
    >
      {/* Backgrounds */}
      <Image
        className="absolute z-[-2] top-[10rem] lg:top-0 w-full left-[50%] translate-x-[-50%] pointer-events-none"
        alt="Grid background"
        src={gridGBimg}
      />
      <Image
        className="faq-section-blur absolute z-[-4] top-[10rem] lg:top-0 pointer-events-none w-full left-[50%] translate-x-[-50%]"
        alt="Blur background"
        src={blurBGimg}
      />
      {/* End backgrounds */}
      <div className="container-lp mx-auto">
        <div
          ref={content1Ref}
          className="flex flex-col items-center justify-end w-full lg:h-[70rem] mb-[4rem] lg:mb-[8.4rem]"
        >
          {data?.faqTexts && (
            <PortableText
              value={data?.faqTexts ?? []}
              components={customRichTextComponent({
                block: {
                  h6: ({ children }) => (
                    <BlackPill
                      text={children as string}
                      className="backdrop-blur-[7.5rem]"
                    />
                  ),
                  h2: ({ children }) => (
                    <h2 className="gradient-title text-[5rem] lg:text-[7rem] font-[600] tracking-[-0.2rem] lg:tracking-[-0.28rem] leading-[110%] lg:leading-[120%] text-center my-[2rem]">
                      {children}
                    </h2>
                  ),
                  normal: ({ children }) => (
                    <p className="text-white text-center text-[2.25rem] font-[400] leading-[3.375rem] opacity-70">
                      {children}
                    </p>
                  ),
                },
              })}
            />
          )}
        </div>

        <Accordion
          ref={content2Ref}
          type="single"
          collapsible
          className="w-full"
        >
          {data?.faqItems?.map((faq, index) => (
            <AccordionItem
              key={index}
              value={`item-${index}`}
              className="faq-items border-none rounded-[1.5rem] px-[4rem] pb-[1rem] pt-[2.5rem] lg:pb-[2rem] lg:pt-[4rem] mb-[2rem] bg-[#ffffff0d] [&_svg]:text-white"
            >
              <AccordionTrigger className="text-white text-left text-[2.25rem] font-[400] leading-[3.375rem] opacity-85 hover:opacity-100 hover:underline transition-all">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-white text-[2.25rem] font-[400] leading-[150%] opacity-60">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
